// Engineer-placed tile improvements rendered as small voxel clusters
// above the terrain tile. Reads `improvement` prop surfaced by the WASM
// snapshot for tiles with CFarm / CForestry / CMine / CVineyard /
// CDruidicCircle components.
//
// Each improvement is a compact 3-5 voxel decoration designed to be
// recognizable at the hex tile scale. Drives no asset pipeline (no PNGs)
// since the procedural voxels are cheaper and fit the voxel aesthetic.

import type { SceneNode } from '@aod/protocol';
import { useMemo } from 'react';
import * as THREE from 'three';
import type { Season } from '../sim/gameTime.js';
import { HEX_HEIGHT } from './HexGrid.js';
import { RenderLayer } from './RenderLayer.js';
import { buildBiomeMap, tileElevationAt } from './elevation.js';
import { seasonPhase } from './seasons.js';

interface TileImprovementsProps {
  readonly tiles: ReadonlyArray<SceneNode>;
  /** 1-based day of year — drives the autumn harvest gold on farms. */
  readonly seasonDay?: number;
}

/** Linear sRGB blend of two hex colours at t in [0,1]. */
function blendHex(a: string, b: string, t: number): string {
  const ca = new THREE.Color(a);
  const cb = new THREE.Color(b);
  ca.lerp(cb, Math.min(1, Math.max(0, t)));
  return `#${ca.getHexString()}`;
}

interface ImprovementVoxels {
  readonly positions: ReadonlyArray<readonly [number, number, number]>;
  readonly color: string;
  readonly size: number;
}

// Each improvement kind: a small cluster of voxel cubes with a distinct
// silhouette and color.
const IMPROVEMENT_VISUALS: Record<string, ImprovementVoxels> = {
  // Farm: rows of furrows (3 brown stripes)
  farm: {
    positions: [
      [-0.25, 0, -0.2],
      [-0.25, 0, 0],
      [-0.25, 0, 0.2],
      [0.25, 0, -0.2],
      [0.25, 0, 0],
      [0.25, 0, 0.2],
    ],
    color: '#7a4a1a',
    size: 0.18,
  },
  // Forestry: stump + small tree
  forestry: {
    positions: [
      [0, 0, 0], // stump
      [0, 0.2, 0], // trunk
      [0, 0.4, 0], // canopy
      [-0.1, 0.5, 0],
      [0.1, 0.5, 0],
    ],
    color: '#3a5a2a',
    size: 0.15,
  },
  // Mine: entrance + cart
  mine: {
    positions: [
      [0, 0, 0],
      [-0.15, 0.15, 0],
      [0.15, 0.15, 0],
      [0, 0.15, 0],
      [0.3, 0, 0.1], // cart
    ],
    color: '#5a4030',
    size: 0.18,
  },
  // Vineyard: T-posts with grape clusters
  vineyard: {
    positions: [
      [-0.2, 0, 0],
      [-0.2, 0.25, 0],
      [0.2, 0, 0],
      [0.2, 0.25, 0],
      [0, 0.25, 0], // crossbeam
      [-0.1, 0.15, 0.05], // grapes
      [0.1, 0.15, 0.05],
    ],
    color: '#5a2a5a',
    size: 0.1,
  },
  // Druidic circle: ring of standing stones with center
  druidic_circle: {
    positions: [
      [0, 0, 0],
      [0.25, 0, 0],
      [-0.25, 0, 0],
      [0, 0, 0.25],
      [0, 0, -0.25],
      [0.18, 0, 0.18],
      [-0.18, 0, 0.18],
      [0.18, 0, -0.18],
      [-0.18, 0, -0.18],
    ],
    color: '#9a9a8a',
    size: 0.12,
  },
};

const BOX_GEO = new THREE.BoxGeometry(1, 1, 1);

export function TileImprovements({ tiles, seasonDay = 0 }: TileImprovementsProps) {
  const biomeMap = useMemo(() => buildBiomeMap(tiles), [tiles]);

  // Harvest: farms ripen gold through autumn, fading as autumn turns to
  // winter (the season fade sits at the season's tail).
  const harvestMix = useMemo(() => {
    if (seasonDay <= 0) return 0;
    const { season, next, t } = seasonPhase(seasonDay);
    const weight = (s: Season) => (s === 'autumn' ? 1 : 0);
    return weight(season) + (weight(next) - weight(season)) * t;
  }, [seasonDay]);

  const improved = useMemo(() => {
    const out: Array<{
      key: string;
      x: number;
      y: number;
      z: number;
      visual: ImprovementVoxels;
      color: string;
    }> = [];
    for (const tile of tiles) {
      const props = tile.props as Record<string, unknown> | undefined;
      const kind = props?.improvement;
      if (typeof kind !== 'string') continue;
      const visual = IMPROVEMENT_VISUALS[kind];
      if (!visual) continue;
      const q = (props?.q as number | undefined) ?? 0;
      const r = (props?.r as number | undefined) ?? 0;
      const [x, , z] = tile.transform.position;
      const elev = tileElevationAt(q, r, biomeMap);
      // Furrows ripen toward ripe wheat at harvest; everything else keeps
      // its season-blind colour.
      const color =
        kind === 'farm' && harvestMix > 0
          ? blendHex('#7a4a1a', '#c9a52e', harvestMix)
          : visual.color;
      out.push({ key: `${tile.id}-${kind}`, x, y: elev + HEX_HEIGHT / 2, z, visual, color });
    }
    return out;
  }, [tiles, biomeMap, harvestMix]);

  if (improved.length === 0) return null;

  return (
    <group renderOrder={RenderLayer.Resources}>
      {improved.flatMap(({ key, x, y, z, visual, color }) =>
        visual.positions.map((pos) => (
          <mesh
            key={`${key}-${pos[0]}-${pos[2]}`}
            position={[x + pos[0], y + pos[1] + visual.size / 2, z + pos[2]]}
            geometry={BOX_GEO}
            scale={[visual.size, visual.size, visual.size]}
            castShadow
          >
            <meshStandardMaterial color={color} roughness={0.85} />
          </mesh>
        )),
      )}
    </group>
  );
}
